<template>
  <div
    v-if="hasSlotContent"
    class="overlay-view-wrapper"
    @click.stop
    @dblclick.stop
    @mouseup.stop
    @mousedown.stop
    @mousemove.stop
    @touchstart.stop
  >
    <div ref="overlayViewRef" v-bind="$attrs">
      <slot />
    </div>
  </div>
</template>

<script lang="ts" setup>
import equal from 'fast-deep-equal'
import { createOverlayViewClass } from '~/utils/map'
import type { OverlayViewClass } from '~/utils/map'

const overlayViewEvents = [
  'click',
  'dblclick',
  'mousedown',
  'mousemove',
  'mouseout',
  'mouseover',
  'mouseup',
] as const

const props = withDefaults(
  defineProps<{
    map: google.maps.Map
    api: typeof google.maps
    position?: google.maps.LatLng | google.maps.LatLngLiteral
    pane?: keyof google.maps.MapPanes
    zIndex?: number
    onAdd?: () => void
    draw?: () => void
    onRemove?: () => void
  }>(),
  {
    pane: 'overlayLayer',
    zIndex: 1,
    onAdd: void 0,
    draw: void 0,
    onRemove: void 0,
  },
)

const { map, api } = toRefs(props)

const emit = defineEmits<{
  click: [MouseEvent]
  dblclick: [MouseEvent]
  mousedown: [MouseEvent]
  mousemove: [MouseEvent]
  mouseout: [MouseEvent]
  mouseover: [MouseEvent]
  mouseup: [MouseEvent]
}>()

const slots = useSlots()

const overlayView = ref<InstanceType<OverlayViewClass>>()
const overlayViewRef = ref<HTMLElement>()

const hasSlotContent = computed(() =>
  slots.default?.().some((vnode) => vnode.type !== Comment),
)

const options = computed(() => ({
  position: props.position,
  map: map.value,
  element: overlayViewRef.value,
  pane: props.pane,
  zIndex: props.zIndex,
}))

const callbacks = computed(() => ({
  onAdd: props.onAdd,
  draw: props.draw,
  onRemove: props.onRemove,
}))

onMounted(() => {
  watchImmediate(
    [map, options, callbacks],
    ([_, newOptions, newCallbacks], [oldMap, oldOptions, oldCallbacks]) => {
      const optionsChanged = !equal(newOptions, oldOptions)
      const callbacksChanged = !equal(newCallbacks, oldCallbacks)
      const mapChanged = map.value !== oldMap

      if (
        map.value &&
        api.value &&
        (optionsChanged || callbacksChanged || mapChanged)
      ) {
        if (overlayView.value) {
          overlayView.value.setOptions(newOptions)
          ;(overlayView.value as any).setCallbacks(newCallbacks)
        } else {
          Object.assign(api.value, {
            CustomOverlayView:
              api.value.CustomOverlayView || createOverlayViewClass(api.value),
          })

          overlayView.value = markRaw(
            new api.value.CustomOverlayView(newOptions, newCallbacks),
          )

          // Add event listeners
          overlayViewEvents.forEach((event) => {
            if (overlayViewRef.value) {
              overlayViewRef.value.addEventListener(event, (e: Event) =>
                emit(event as any, e as MouseEvent),
              )
            }
          })
        }
      }
    },
  )
})

onBeforeUnmount(() => {
  if (overlayView.value) {
    api.value?.event.clearInstanceListeners(overlayView.value)
    overlayView.value.setMap(null)
  }
})

defineExpose({
  overlayView,
  getProjection: () => overlayView.value?.getProjection(),
  getPanes: () => overlayView.value?.getPanes(),
  draw: () => overlayView.value?.draw(),
})
</script>

<style scoped>
.overlay-view-wrapper {
  @apply hidden;
}

:deep(.mapdiv) .overlay-view-wrapper {
  @apply inline-block;
}
</style>
